<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import CoLivingTabs from './CoLivingTabs.vue';
import TeamModal from './components/TeamModal.vue';
import { apiErrorCode, apiErrorMessage } from '../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from './constants';
import { coverageByDay } from './teamForm';
import { pageToStepBackTo } from './helpers';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SEARCH_DELAY_MS = 300;

export default {
  name: 'CoLivingTeamPage',
  components: {
    PageHeader,
    PaginationBar,
    LoadingState,
    DeleteModal,
    CoLivingTabs,
    TeamModal,
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      search: '',
      roleFilter: '',
      condoFilter: '',
      statusFilter: 'all',
      page: 1,
      pageSize: PAGE_SIZE_OPTIONS[0],
      loadError: '',
      isModalOpen: false,
      editingMember: null,
      memberToDelete: null,
      isDeleting: false,
      searchTimer: null,
    };
  },
  computed: {
    ...mapGetters('coLivingTeam', {
      members: 'getMembers',
      meta: 'getMeta',
      uiFlags: 'getUiFlags',
    }),
    ...mapGetters('coLivingCatalog', { roles: 'getRoles' }),
    ...mapGetters('coLivingCondos', { condos: 'getCondos' }),
    totalPages() {
      return Math.max(1, Math.ceil(this.meta.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
    query() {
      const query = { page: this.page, page_size: this.pageSize };
      if (this.search.trim()) query.search = this.search.trim();
      if (this.roleFilter) query.role = this.roleFilter;
      if (this.condoFilter) query.site_id = this.condoFilter;
      if (this.statusFilter !== 'all') query.status = this.statusFilter;
      return query;
    },
  },
  mounted() {
    this.fetchRoles();
    this.fetchCondos();
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    ...mapActions('coLivingTeam', ['fetchMembers', 'removeMember']),
    ...mapActions('coLivingCatalog', ['fetchRoles']),
    ...mapActions('coLivingCondos', ['fetchCondos']),
    async load() {
      this.loadError = '';
      try {
        const data = await this.fetchMembers(this.query);
        const back = pageToStepBackTo(data, this.page, this.pageSize);
        if (back) {
          this.page = back;
          await this.load();
        }
      } catch (err) {
        this.loadError = apiErrorMessage(
          this,
          err,
          'CO_LIVING.COMMON.LOAD_FAILED'
        );
      }
    },
    // A new filter starts again from the first page.
    onFilterChange() {
      this.page = 1;
      this.load();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.onFilterChange, SEARCH_DELAY_MS);
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.onFilterChange();
    },
    condoName(siteId) {
      if (!siteId) return this.$t('CO_LIVING.TEAM.COVERAGE.ANY_CONDO');
      const condo = this.condos.find(c => c.id === siteId);
      return condo ? condo.name : siteId;
    },
    coverageDays(member) {
      return coverageByDay(member.coverage);
    },
    openAdd() {
      this.editingMember = null;
      this.isModalOpen = true;
    },
    openEdit(member) {
      this.editingMember = member;
      this.isModalOpen = true;
    },
    // A PUT answers with `visits_flagged` (new leave hit booked visits) and
    // `reroute` (the member was deactivated): tell the admin what moved.
    onSaved(result) {
      useAlert(this.$t('CO_LIVING.TEAM.SAVED'));
      const flagged = (result && result.visits_flagged) || [];
      if (flagged.length) {
        useAlert(
          this.$t('CO_LIVING.TEAM.VISITS_FLAGGED', { count: flagged.length })
        );
      }
      const reroute = result && result.reroute;
      if (reroute) {
        const moved = (reroute.rerouted || []).length;
        const parked = (reroute.needs_admin || []).length;
        if (moved || parked) {
          useAlert(this.$t('CO_LIVING.TEAM.REROUTED', { moved, parked }));
        }
      }
      this.load();
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.removeMember(this.memberToDelete.id);
        useAlert(this.$t('CO_LIVING.TEAM.DELETED'));
        this.load();
      } catch (err) {
        useAlert(
          apiErrorCode(err) === 'in_use'
            ? this.$t('CO_LIVING.TEAM.IN_USE')
            : apiErrorMessage(this, err, 'CO_LIVING.TEAM.DELETE_FAILED')
        );
      } finally {
        this.isDeleting = false;
        this.memberToDelete = null;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-6" data-testid="co-living-team-page">
    <PageHeader
      :title="$t('CO_LIVING.TITLE')"
      icon="condo"
      :description="$t('CO_LIVING.DESCRIPTION')"
    />
    <CoLivingTabs />

    <div class="flex items-start justify-between gap-4 mb-4">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ $t('CO_LIVING.TEAM.INTRO') }}
      </p>
      <woot-button
        icon="add"
        data-testid="co-living-add-member"
        @click="openAdd"
      >
        {{ $t('CO_LIVING.TEAM.ADD_MEMBER') }}
      </woot-button>
    </div>

    <div class="flex flex-wrap gap-3 mb-4">
      <input
        v-model="search"
        type="search"
        :placeholder="$t('CO_LIVING.TEAM.SEARCH')"
        class="max-w-xs"
        :class="[fieldClass]"
        data-testid="co-living-member-search"
        @input="onSearchInput"
      />
      <select
        v-model="roleFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-member-role-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TEAM.ALL_ROLES') }}</option>
        <option v-for="role in roles" :key="role.key" :value="role.key">
          {{ role.label }}
        </option>
      </select>
      <select
        v-model="condoFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-member-condo-filter"
        @change="onFilterChange"
      >
        <option value="">{{ $t('CO_LIVING.TEAM.ALL_CONDOS') }}</option>
        <option v-for="condo in condos" :key="condo.id" :value="condo.id">
          {{ condo.name }}
        </option>
      </select>
      <select
        v-model="statusFilter"
        class="max-w-xs"
        :class="[selectClass]"
        data-testid="co-living-member-status-filter"
        @change="onFilterChange"
      >
        <option value="all">{{ $t('CO_LIVING.COMMON.ALL_STATUSES') }}</option>
        <option value="active">{{ $t('CO_LIVING.COMMON.ACTIVE') }}</option>
        <option value="inactive">{{ $t('CO_LIVING.COMMON.INACTIVE') }}</option>
      </select>
    </div>

    <LoadingState
      v-if="uiFlags.isFetching && !members.length"
      :message="$t('CO_LIVING.COMMON.LOADING')"
    />
    <p
      v-else-if="loadError"
      class="text-sm text-red-500"
      data-testid="co-living-load-error"
    >
      {{ loadError }}
    </p>
    <p
      v-else-if="!members.length"
      class="text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-team-empty"
    >
      {{ $t('CO_LIVING.TEAM.EMPTY') }}
    </p>
    <template v-else>
      <div
        class="overflow-x-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
      >
        <table class="w-full text-sm" data-testid="co-living-team-table">
          <thead>
            <tr class="text-left text-slate-500 dark:text-slate-400">
              <th class="p-3">{{ $t('CO_LIVING.TEAM.COLUMNS.MEMBER') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.TEAM.COLUMNS.CONTACT') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.TEAM.COLUMNS.ADDRESS') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.TEAM.COLUMNS.HOURS') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.TEAM.COLUMNS.COVERAGE') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.TEAM.COLUMNS.OPEN') }}</th>
              <th class="p-3">{{ $t('CO_LIVING.COMMON.STATUS') }}</th>
              <th class="p-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="member in members"
              :key="member.id"
              class="border-t border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 align-top"
              :data-testid="`co-living-member-row-${member.id}`"
            >
              <td class="p-3">
                {{ member.name }}
                <span class="block text-xs text-slate-500">
                  {{ member.role_label }}
                </span>
              </td>
              <td class="p-3">
                {{ member.phone }}
                <span class="block text-xs text-slate-500">
                  {{ member.email || $t('CO_LIVING.TEAM.NO_EMAIL') }}
                </span>
              </td>
              <td class="p-3">
                {{ member.address }}
                <span
                  v-if="!member.located"
                  class="block text-xs text-amber-600 dark:text-amber-400"
                >
                  {{ $t('CO_LIVING.TEAM.NOT_LOCATED') }}
                </span>
              </td>
              <td class="p-3">
                {{ member.hours || '-' }}
                <span
                  v-for="leave in member.leave"
                  :key="leave.id"
                  class="block text-xs text-slate-500"
                >
                  {{
                    $t('CO_LIVING.TEAM.LEAVE_RANGE', {
                      from: leave.date_from,
                      to: leave.date_to,
                    })
                  }}
                </span>
              </td>
              <td class="p-3">
                <span
                  v-if="member.office_based"
                  class="text-xs text-slate-500"
                  :data-testid="`co-living-member-office-${member.id}`"
                >
                  {{ $t('CO_LIVING.TEAM.OFFICE_BASED') }}
                </span>
                <span
                  v-for="day in member.office_based ? [] : coverageDays(member)"
                  :key="day.weekday"
                  class="block text-xs"
                >
                  <b>{{
                    $t(`CO_LIVING.WEEKDAYS.${day.weekday}`).slice(0, 3)
                  }}</b>
                  {{
                    day.rows
                      .map(
                        row =>
                          `${condoName(row.site_id)} ${row.start}-${row.end}`
                      )
                      .join(', ')
                  }}
                </span>
              </td>
              <td class="p-3">{{ member.open_ticket_count }}</td>
              <td class="p-3">
                {{
                  member.active
                    ? $t('CO_LIVING.COMMON.ACTIVE')
                    : $t('CO_LIVING.COMMON.INACTIVE')
                }}
              </td>
              <td class="p-3 text-right whitespace-nowrap">
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.EDIT')"
                  variant="clear"
                  size="small"
                  icon="edit"
                  :data-testid="`co-living-member-edit-${member.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.EDIT')"
                  @click="openEdit(member)"
                />
                <woot-button
                  v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
                  variant="clear"
                  size="small"
                  color-scheme="alert"
                  icon="delete"
                  :data-testid="`co-living-member-delete-${member.id}`"
                  :aria-label="$t('CO_LIVING.COMMON.DELETE')"
                  @click="memberToDelete = member"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationBar
        :pagination="pagination"
        :page-size-options="pageSizeOptions"
        :loading="uiFlags.isFetching"
        @previous="goToPage(page - 1)"
        @next="goToPage(page + 1)"
        @page-size-change="onPageSizeChange"
      />
    </template>

    <TeamModal
      v-if="isModalOpen"
      :member="editingMember"
      @close="isModalOpen = false"
      @saved="onSaved"
    />
    <DeleteModal
      v-if="memberToDelete"
      :delete-name="memberToDelete.name"
      :is-loading="isDeleting"
      @close="memberToDelete = null"
      @confirm="confirmDelete"
    >
      <p class="mt-2 text-sm text-slate-500">
        {{ $t('CO_LIVING.TEAM.DELETE_NOTE') }}
      </p>
    </DeleteModal>
  </div>
</template>
